import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { FIXED_SERIES_COLORS } from '../config/seriesConfig';
import type { DiagramSeriesGroup, DiagramSeriesGroupId } from '../types/DiagramSeries.types';

type PickColor = (index: number) => string | undefined;

function getGroupSeriesIds(groups: DiagramSeriesGroup[], groupId: DiagramSeriesGroupId): string[] {
  return groups.find((group) => group.id === groupId)?.series.map((series) => series.id) ?? [];
}

/**
 * Colours are stable per series, not per selection: the same channel always gets the same colour,
 * whether it is selected, highlighted or only shown as a lane in the range track.
 *
 * - Fixed series (see FIXED_SERIES_COLORS) take the semantic palette.
 * - Measure channels take the chart palette in list order, so up to `palette.length` channels are distinct.
 * - States take the palette from the other end, so they rarely collide with the first channels.
 */
function buildColorMap(groups: DiagramSeriesGroup[], theme: Theme): Map<string, string> {
  const palette = theme.palette.chartSeries ?? [];
  const colorBySeriesId = new Map<string, string>();

  const assign = (seriesIds: string[], pick: PickColor) => {
    seriesIds.forEach((seriesId, index) => {
      const color = FIXED_SERIES_COLORS[seriesId]?.(theme) ?? pick(index);

      if (color) {
        colorBySeriesId.set(seriesId, color);
      }
    });
  };

  assign(getGroupSeriesIds(groups, 'speed'), () => undefined);
  assign(getGroupSeriesIds(groups, 'channels'), (index) => palette[index % palette.length]);
  assign(getGroupSeriesIds(groups, 'states'), (index) => palette[palette.length - 1 - (index % palette.length)]);

  return colorBySeriesId;
}

export function useDiagramSeriesColors(groups: DiagramSeriesGroup[]) {
  const theme = useTheme();

  return useMemo(() => {
    const colorBySeriesId = buildColorMap(groups, theme);

    return {
      getSeriesColor: (seriesId: string) => colorBySeriesId.get(seriesId) ?? theme.palette.text.disabled,
    };
  }, [groups, theme]);
}
